import { isClient, isRSC, isServer, isWeb } from "@tamagui/constants";
import { useContext, useEffect, useMemo, useState } from "react";
import { getConfig } from "../config.js";
import { isDevTools } from "../constants/isDevTools.js";
import { createProxy } from "../helpers/createProxy.js";
import {
  ThemeManager,
  hasNoThemeUpdatingProps
} from "../helpers/ThemeManager.js";
import { ThemeManagerContext } from "../helpers/ThemeManagerContext.js";
import { GetThemeUnwrapped } from "./getThemeUnwrapped.js";
import { useServerRef } from "./useServerHooks.js";
const emptyProps = { name: null };
function getDefaultThemeProxied() {
  const config = getConfig();
  const name = Object.keys(config.themes)[0];
  return getThemeProxied({
    theme: config.themes[name],
    name
  });
}
const useTheme = (props = emptyProps) => {
  var _a;
  return (isRSC ? null : (_a = useThemeWithState(props)) == null ? void 0 : _a.theme) || getDefaultThemeProxied();
};
const useThemeWithState = (props) => {
  var _a;
  const keys = useServerRef([]);
  const changedTheme = useChangeThemeEffect(
    props,
    false,
    isClient ? () => {
      var _a2;
      return ((_a2 = props.shouldUpdate) == null ? void 0 : _a2.call(props)) ?? keys.current.length === 0;
    } : void 0
  );
  const { themeManager, isNewTheme, theme, name, className } = changedTheme;
  if (process.env.NODE_ENV === "development") {
    if (isWeb && isNewTheme && className === ((_a = themeManager == null ? void 0 : themeManager.parentManager) == null ? void 0 : _a.state.className)) {
      console.error("Should always change, duplicating ThemeMananger bug", themeManager);
    }
    if (props.debug === "verbose") {
      console.groupCollapsed("  \u{1F539} useTheme =>", name);
      const logs = { ...props, name, className, ...isDevTools && { theme } };
      for (const key in logs) {
        console.log("  ", key, logs[key]);
      }
      console.groupEnd();
    }
  }
  if (!changedTheme.theme) {
    if (process.env.NODE_ENV === "development") {
      console.warn("No theme found", name, props, themeManager);
    }
    return null;
  }
  const proxiedTheme = useMemo(() => {
    return getThemeProxied(changedTheme);
  }, [theme, isNewTheme, name, className, themeManager]);
  return {
    ...changedTheme,
    theme: proxiedTheme
  };
};
function getThemeProxied({
  theme,
  themeManager,
  keys
}) {
  return createProxy(theme, {
    has(_, key) {
      if (typeof key === "string") {
        if (key[0] === "$")
          key = key.slice(1);
        return themeManager == null ? void 0 : themeManager.allKeys.has(key);
      }
      return Reflect.has(theme, key);
    },
    get(_, key) {
      if (key === GetThemeUnwrapped) {
        return theme;
      }
      if (key === "undefined" || key === "__proto__" || key === "$typeof" || typeof key !== "string" || !themeManager) {
        return Reflect.get(_, key);
      }
      const keyString = key;
      if (key[0] === "$") {
        key = key.slice(1);
      }
      const val = themeManager.getValue(key);
      const currentKeys = keys == null ? void 0 : keys.current;
      if (val && currentKeys) {
        return new Proxy(val, {
          // when they touch the actual value we only track it
          // if its a variable (web), its ignored!
          get(_2, subkey) {
            if (subkey === "val" && !currentKeys.includes(keyString)) {
              currentKeys.push(keyString);
            }
            return Reflect.get(val, subkey);
          }
        });
      }
      return val;
    }
  });
}
const activeThemeManagers = /* @__PURE__ */ new Set();
const useChangeThemeEffect = (props, root = false, disableUpdate) => {
  if (isRSC) {
    return {
      ...createState().state,
      themeManager: null
    };
  }
  const parentManager = useContext(ThemeManagerContext);
  const {
    debug,
    // @ts-expect-error internal use only
    disable
  } = props;
  if (disable) {
    if (!parentManager)
      throw `\u274C`;
    return {
      ...parentManager.state,
      isNewTheme: false,
      themeManager: parentManager
    };
  }
  const [themeState, setThemeState] = useState(createState);
  const { isNewTheme, state, themeManager, mounted } = themeState;
  if (!isServer) {
    useEffect(() => {
      if (disable)
        return;
      if (props.inverse && !mounted) {
        setThemeState({ ...themeState, mounted: true });
        return;
      }
      activeThemeManagers.add(themeManager);
      return () => {
        activeThemeManagers.delete(themeManager);
      };
    }, [disable, state, isNewTheme, debug]);
  }
  const isInversingOnMount = Boolean(!themeState.mounted && props.inverse);
  const shouldReturnParentState = hasNoThemeUpdatingProps(props) || isInversingOnMount;
  if (shouldReturnParentState) {
    if (!parentManager)
      throw "impossible";
    if (process.env.NODE_ENV === "development" && props.debug === "verbose")
      console.log("useTheme hasNoThemeUpdatingProps", parentManager.state.name, "isInversingOnMount", isInversingOnMount);
    return {
      ...parentManager.state,
      className: isInversingOnMount ? "" : parentManager.state.className,
      themeManager: parentManager,
      isNewTheme: false
    };
  }
  updateState();
  return {
    ...state,
    isNewTheme,
    themeManager
  };
  function createState(prev) {
    if (prev && (disableUpdate == null ? void 0 : disableUpdate())) {
      return prev;
    }
    const _ = new ThemeManager(props, root ? "root" : parentManager);
    const isNewTheme2 = _ !== parentManager;
    const mounted2 = !props.inverse ? true : root || (prev == null ? void 0 : prev.mounted);
    return {
      // ThemeManager returns parentManager if no change
      isNewTheme: isNewTheme2,
      state: { ..._.state },
      themeManager: _,
      mounted: mounted2
    };
  }
  function updateState(updatingManager) {
    if (disableUpdate == null ? void 0 : disableUpdate()) {
      return;
    }
    const manager = updatingManager ?? themeManager;
    const next = manager.getState(props, parentManager);
    const shouldChange = manager.getStateShouldChange(next, isNewTheme ? state : null);
    if (shouldChange) {
      setThemeState(createState);
    } else {
      if (!next && (parentManager == null ? void 0 : parentManager.state.name) !== state.name) {
        setThemeState(createState);
      }
    }
  }
};
export {
  activeThemeManagers,
  getThemeProxied,
  useChangeThemeEffect,
  useTheme,
  useThemeWithState
};
//# sourceMappingURL=useTheme.js.map
